import { useState, useEffect, useCallback } from 'react';
import { App as CapApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { isNative } from '../utils/platformUtils';
import { useDataContext } from '../data/DataProvider';
import { HANDOFF_TOKEN_PARAM } from '../config/appLinks';
import { exchangeHandoffToken, previewHandoffToken } from '../lib/beta';
import {
  confirmInviteToken,
  markSilentSignIn,
  getSessionUser,
  isSignedInUser,
} from '../lib/authAccount';
import { markInstallReferrerRead, takeInstallReferrerToken } from '../lib/installReferrer';
import { getAllUserData, countLocalCreations } from '../data/userDataStore';
import { hydrateUserMirror } from '../data/userStore';
import { syncUserDataWithRetry } from '../data/syncEngine';
import { getAccessToken } from '../lib/supabaseClient';
import { API_URL } from '../config/api';
import { track } from '../utils/analytics';

// Le jeton de passage recu par l'app (spec v5, S4). Depuis la sortie des
// universal links, le lien d'invitation https ouvre TOUJOURS le navigateur :
// le choix de compte vit a 100 % sur le web (interstitiel S3), et l'app ne
// recoit plus jamais un choix d'identite. Ce qui lui arrive, par le bouton
// "Open the app" (scheme upscale://invite?h=...), c'est un jeton de passage a
// usage unique emis pour le compte CHOISI sur la page : il ne sait qu'ouvrir
// une session, l'acces est deja pose cote compte. Aucun code de rachat ne
// voyage dans ce chemin (App Store 3.1.1).
//
// Deux livraisons possibles pour la meme URL, comme pour le retour Patreon :
//  - a chaud : l'app tournait, appUrlOpen se declenche ;
//  - a froid : le lien lance l'app et l'URL n'arrive QUE par getLaunchUrl().
//
// Trois etats possibles au moment ou le jeton arrive :
//  - invite vierge : echange, purge sans consequence, rechargement ;
//  - invite qui a deja sauve des choses : le dialogue nomme le compte choisi
//    et demande AVANT d'echanger ("Not now" ne brule aucun usage) ;
//  - compte deja connecte : on ne bascule rien, on le dit si le jeton porte un
//    autre compte, et l'acces pose par le serveur arrive par la sync (F15).

const MESSAGES = {
  invalid: 'This invitation link has expired. Sign in with your email instead.',
  network: "We couldn't reach the server. Check your connection and try again.",
  exchange: 'We could not sign you in from that link. Sign in with your email instead.',
};

// Question posee a l'invite anonyme qui a deja sauve des choses : l'echange
// bascule l'app sur le compte choisi et purge ce qui est ici. Le compte est
// NOMME (le preview l'a lu sans rien bruler) et le nombre de sauvegardes
// aussi : la personne sait exactement ce qu'elle accepte.
export function guestSwitchPrompt(email, count) {
  const plural = count === 1 ? 'pattern stays' : 'patterns stay';
  return {
    title: `Sign in as ${email}?`,
    body: `You'll start fresh on this account. Your ${count} saved ${plural} on this device's guest profile.`,
  };
}

// Deuxieme entree du meme parcours, Android seulement : le referrer
// d'installation de Google Play. La page d'invitation a mis le jeton dans le
// lien du store, Play le rend au premier lancement, et l'app s'ouvre connectee
// sans que personne ait rien touche (cf. lib/installReferrer).
//
// Meme suite exactement que le lien : claim, session, marque, rechargement.
// Une seule difference, et elle compte : ce chemin est MUET. Personne n'a
// clique, personne n'attend de reponse ; un "we could not sign you in" au tout
// premier lancement, sur un referrer perime ou etranger, ferait peur pour rien.
const REFERRER_URL_PREFIX = 'referrer:';

// Deux gardes contre le rejeu, memes raisons que usePatreonReturn.

// 1. Meme URL livree deux fois dans la meme session JS (getLaunchUrl + appUrlOpen
//    au demarrage a froid).
const handledUrls = new Set();

// 2. getLaunchUrl() rend l'URL de lancement indefiniment, y compris apres un
//    rechargement : on ne la lit qu'une fois par session de webview.
const LAUNCH_READ_KEY = 'upscale_invite_launch_read';

export function parseHandoffLink(url) {
  try {
    const parsed = new URL(url);
    const token = (parsed.searchParams.get(HANDOFF_TOKEN_PARAM) || '').trim();
    // Sans jeton de passage, ce lien n'a rien a echanger : le scheme nu ouvre
    // l'app, et c'est tout ce qu'on attend de lui. Les parametres du vieux
    // lien d'invitation (`invite`, `t`) sont volontairement ignores : l'app ne
    // sait plus ouvrir que le compte choisi sur le web.
    if (!token) return null;
    return { token };
  } catch {
    return null;
  }
}

function claimUrl(url) {
  if (handledUrls.has(url)) return false;
  handledUrls.add(url);
  return true;
}

function claimLaunchRead() {
  try {
    if (sessionStorage.getItem(LAUNCH_READ_KEY)) return false;
    sessionStorage.setItem(LAUNCH_READ_KEY, '1');
  } catch {
    // Storage indisponible : le dedoublonnage par URL reste actif.
  }
  return true;
}

// L'acces que le serveur vient de poser sur le compte connecte, tire tout de
// suite : une passe de sync, puis la re-hydratation du miroir, exactement ce que
// fait le boot (DataProvider). Le verrou s'ouvre sans rechargement, donc sans
// interrompre ce que la personne etait en train de faire.
//
// Deux tentatives au plus, et jamais d'erreur remontee : un echec ici ne dit
// rien a personne (le compte EST connecte, le serveur a bien pose l'acces), et
// le boot suivant repassera. Laisser filer une erreur ferait afficher "we could
// not sign you in" a quelqu'un qui vient de lire son adresse a l'ecran.
async function pullAccessRow(db) {
  if (!db) return;
  try {
    await syncUserDataWithRetry(db, {
      apiUrl: API_URL,
      getToken: getAccessToken,
      maxAttempts: 2,
    });
    hydrateUserMirror(await getAllUserData(db));
  } catch (err) {
    console.warn('[invite] sync de l acces echouee:', err.message);
  }
}

export function useInviteLink() {
  const { db } = useDataContext();
  const [notice, setNotice] = useState(null); // { severity, message }
  const [pending, setPending] = useState(false);
  // { onContinue, onCancel } quand la question est posee, null sinon.
  const [guestConfirm, setGuestConfirm] = useState(null);

  // La reponse de l'utilisateur, attendue comme une valeur : le parcours reste
  // une suite lisible, du lien au rechargement. Le dialogue porte le compte
  // choisi et le nombre de sauvegardes a laisser derriere.
  const askGuestConfirm = useCallback(
    (email, count) =>
      new Promise((resolve) => {
        const answer = (ok) => {
          setGuestConfirm(null);
          resolve(ok);
        };
        setGuestConfirm({
          email,
          count,
          onContinue: () => answer(true),
          onCancel: () => answer(false),
        });
      }),
    []
  );

  // silent : le referrer d'installation passe par ici sans jamais rien afficher
  // d'autre que le voile et le toast de connexion. via : d'ou vient le jeton,
  // pour que le tunnel separe le lien clique du referrer subi.
  const handle = useCallback(
    async (url, link, { silent = false, via = 'native_link' } = {}) => {
      const warn = (severity, message) => {
        if (!silent) setNotice({ severity, message });
      };
      // Compte deja connecte sur cet appareil (F15) : on ne bascule JAMAIS sur
      // un clic de lien, et on n'echange pas le jeton non plus (il reste vif).
      // L'activation d'un autre compte se fait sur le web.
      let signedInUser = null;
      try {
        const user = await getSessionUser();
        if (isSignedInUser(user)) signedInUser = user;
      } catch {
        // Session illisible : on continue comme un invite, le serveur tranchera.
      }

      if (signedInUser) {
        const previewed = await previewHandoffToken(link.token);
        if (!previewed.success) {
          track('invite_failed', { step: 'preview', reason: previewed.error, via });
          warn('warning', MESSAGES[previewed.error] || MESSAGES.invalid);
          return;
        }
        // Le jeton porte un autre compte : on le dit, sans bascule. Le meme
        // compte : rien a dire, l'acces arrive par la sync dans les deux cas,
        // sans rechargement.
        const sameAccount =
          (signedInUser.email || '').trim().toLowerCase() === previewed.email.toLowerCase();
        if (!sameAccount) warn('info', `Already signed in as ${signedInUser.email}.`);
        await pullAccessRow(db);
        return;
      }

      // Invite anonyme qui a deja sauve des choses : l'echange va purger le
      // local (switchIdentity), il faut son accord avant. La question passe
      // AVANT l'echange, et elle NOMME le compte : le preview lit le jeton
      // sans le bruler, un "Not now" ne coute donc aucun usage.
      const creations = await countLocalCreations(db);
      if (creations > 0) {
        const previewed = await previewHandoffToken(link.token);
        if (!previewed.success) {
          track('invite_failed', { step: 'preview', reason: previewed.error, via });
          warn('warning', MESSAGES[previewed.error] || MESSAGES.invalid);
          return;
        }
        const go = await askGuestConfirm(previewed.email, creations);
        if (!go) {
          // Le jeton n'a pas servi : on relache la garde pour qu'un second
          // clic reste possible dans la meme session. Apres l'attente, jamais
          // avant : au lancement a froid la meme URL est livree deux fois, et
          // la relacher plus tot reposerait la question aussitot.
          handledUrls.delete(url);
          track('invite_declined', { via });
          return;
        }
      }

      setPending(true);
      try {
        const exchanged = await exchangeHandoffToken(link.token);
        if (!exchanged.success) {
          track('invite_failed', { step: 'exchange', reason: exchanged.error, via });
          warn('warning', MESSAGES[exchanged.error] || MESSAGES.invalid);
          setPending(false);
          return;
        }

        track('invite_redeemed', { via });

        // Aucun rachat ici : l'acces vit deja sur le compte choisi, pose par
        // l'activation web. L'app ne fait que se connecter puis constater
        // (App Store 3.1.1).

        // Ouvre la session du compte choisi. Purge au passage les donnees de
        // l'invite local : elles appartiennent a un autre identifiant.
        await confirmInviteToken(db, exchanged.tokenHash);

        // Le rechargement efface tout etat memoire : la marque traverse pour
        // que le boot suivant annonce sous quelle adresse l'app revient. Sans
        // elle, l'app se rouvre connectee a un compte que personne n'a choisi a
        // l'ecran, en silence.
        markSilentSignIn();

        // Le splash couvre le rechargement. Sans lui, la webview se vide et
        // l'invite regarde trois secondes de noir juste apres avoir ouvert son
        // invitation : le premier contact avec l'app ressemble a un plantage.
        // iOS ne rejoue pas l'ecran de lancement natif sur un reload de
        // webview, il faut le redemander. Le boot suivant le retire lui-meme
        // (App.js appelle hide() une fois l'app prete), et un echec ici ne doit
        // pas empecher le rechargement, qui est le vrai travail.
        try {
          await SplashScreen.show({ autoHide: false });
        } catch {
          // Pas de splash : on recharge quand meme, en noir.
        }

        // `pending` reste vrai : le voile tient jusqu'au rechargement, et le
        // splash prend le relais. Le baisser ici rendrait l'app verrouillee a
        // l'ecran pendant la fraction de seconde qui precede.
        window.location.reload();
      } catch (err) {
        track('invite_failed', { step: 'signin', reason: 'exchange', via });
        warn('error', MESSAGES.exchange);
        setPending(false);
      }
    },
    [db, askGuestConfirm]
  );

  useEffect(() => {
    // Hors natif, ce parcours n'existe pas : le navigateur ouvre la page
    // d'invitation, qui fait le meme travail avant meme que l'app soit montee.
    if (!isNative()) return undefined;

    // Rien tant que la base locale n'est pas ouverte. Le lancement A FROID par
    // le lien arrive avant elle : getLaunchUrl() repond en quelques
    // millisecondes, `db` vaut encore null, et la bascule d'identite qui suit
    // l'echange va justement purger cette base. On perdait le parcours entre
    // les deux : la session GoTrue etait bien posee, puis la purge jetait sur
    // un `db` nul, et l'invite lisait "we could not sign you in" avec l'acces
    // toujours verrouille, alors qu'il etait connecte.
    //
    // Aucun garde n'est consomme avant ce point : l'URL de lancement sera
    // relue au passage suivant, quand la base sera la. Un lien recu A CHAUD ne
    // passe pas par cette fenetre, la base est ouverte depuis longtemps.
    if (!db) return undefined;

    // Un lien a-t-il pris la main sur ce demarrage ? Le referrer porte le meme
    // jeton que lui : il n'y a pas deux parcours a jouer.
    let linkTookOver = false;

    const consume = (url) => {
      const link = parseHandoffLink(url);
      // Deep link etranger au handoff (retour OAuth, Patreon, vieux lien
      // d'invitation) : rien ici.
      if (!link || !claimUrl(url)) return;
      linkTookOver = true;
      // Sans cette marque, le lancement suivant relirait le referrer et
      // brulerait un second usage du meme lien, pour rien.
      markInstallReferrerRead();
      handle(url, link);
    };

    // Le referrer Play porte le meme jeton de passage que "Open the app"
    // (S5) : le meme echange, joue sans un mot. Un jeton mort (expire, deja
    // echange, referrer organique) ne dit donc rien non plus : personne n'a
    // clique ici, personne n'attend de reponse.
    const consumeReferrer = async () => {
      if (linkTookOver) return;
      const token = await takeInstallReferrerToken();
      if (!token || linkTookOver) return;
      const url = `${REFERRER_URL_PREFIX}${token}`;
      if (!claimUrl(url)) return;
      handle(url, { token }, { silent: true, via: 'install_referrer' });
    };

    let remove = null;
    CapApp.addListener('appUrlOpen', ({ url }) => consume(url)).then((listener) => {
      remove = () => listener.remove();
    });

    // Le referrer part apres l'URL de lancement, jamais avant : c'est la seule
    // facon de savoir si un lien a deja pris la main sur ce demarrage.
    if (claimLaunchRead()) {
      CapApp.getLaunchUrl?.()
        .then((res) => {
          if (res?.url) consume(res.url);
        })
        .catch(() => {})
        .then(consumeReferrer);
    } else {
      consumeReferrer();
    }

    return () => remove?.();
  }, [handle, db]);

  return { notice, pending, guestConfirm, dismissNotice: () => setNotice(null) };
}

export default useInviteLink;
